import { useEffect, useState, useMemo, useRef } from 'react';
declare global {
  interface Window {
    StitchFixAndroidApp?: {
      /**
       * Ask the android device if push notifications are enabled.
       * `handleNotificationPermission` will then be called with the result.
       */
      checkNotificationPermission?: () => void;
      handleNotificationPermission?: (isNotificationsEnabled: boolean) => void;

      /**
       * Request the android device to pop-up a dialog asking the user to enable push notifications.
       * `handleRequestNotificationPermission` will then be called with the result.
       */
      requestNotificationPermission?: () => void;
      handleRequestNotificationPermission?: (isNotificationsEnabled: boolean) => void;

      /**
       * Trigger Android native sharing functionality.
       * Accepts an image URL to share via Android share sheet.
       *
       * Keep this argument backward compatible. Older app versions download
       * and share the image directly; newer versions may create and share a
       * public Vision URL while retaining a save-image action.
       */
      shareImage?: (url: string) => void;
    };
  }
}
const getAndroidApp = () => typeof window === 'object' && window.StitchFixAndroidApp;

/**
 * Returns whether or not the the browser is being embedded within the Android app
 * by testing to see if the Android native bridge exists
 * @returns {boolean}
 */
export const isAndroidApp = () => typeof getAndroidApp() === 'object';

/**
 * Hook for interacting with Android push notifications.
 *
 * @returns {object} An object containing:
 * - `canRequestNotifications`: Whether we are in the Android app and can request the user enable push notifications.
 * - `notificationsEnabled`: Whether notifications are enabled.
 * - `requestNotificationPermission`: A function to request notification permission (pops open a dialog menu in the app).
 */
export const useAndroidPushNotifications = () => {
  const [isEnabled, setIsEnabled] = useState<boolean | null>(null);
  const initialIsEnabled = useRef(isEnabled);
  if (initialIsEnabled.current === null && isEnabled !== null) {
    initialIsEnabled.current = isEnabled;
  }
  const androidApp = useMemo(() => getAndroidApp(), []);
  const {
    checkNotificationPermission
  } = androidApp || {};
  const requestNotificationPermission = androidApp && typeof androidApp === 'object' ? function () {
    androidApp?.requestNotificationPermission?.();
  } : undefined;
  const canCheckPermissions = checkNotificationPermission && typeof checkNotificationPermission === 'function';
  const canRequestPermissions = requestNotificationPermission && typeof requestNotificationPermission === 'function';

  // Request android to check if notifications are enabled, and update state in callback:
  useEffect(() => {
    if (!androidApp) {
      return;
    }

    // Callback after `checkNotificationPermission` is called
    androidApp.handleNotificationPermission = (isNotificationsEnabled: boolean) => {
      setIsEnabled(isNotificationsEnabled);
    };

    // Callback after `requestNotificationPermission` is called
    androidApp.handleRequestNotificationPermission = (isNotificationsEnabled: boolean) => {
      setIsEnabled(isNotificationsEnabled);
    };
    androidApp.checkNotificationPermission?.();
  }, [androidApp]);
  if (!androidApp || !canCheckPermissions || !canRequestPermissions) {
    return {
      canRequestNotifications: false,
      notificationsEnabled: null,
      initialNotificationsEnabled: null,
      requestNotificationPermission: () => {
        throw new Error('Cannot request notification permission: Android window functions not available');
      }
    };
  }
  return {
    canRequestNotifications: true,
    notificationsEnabled: isEnabled,
    initialNotificationsEnabled: initialIsEnabled.current,
    requestNotificationPermission
  };
};